import { useNavigate } from "react-router-dom";
import "./InfoPage.css";

export default function InfoPage() {
  const navigate = useNavigate();

  return (
    <div className="ipx">
      <header className="ipx-header">
        <h1 className="ipx-title">
Fast Pipelines, Evolving Risks: Evolution-Aware Vulnerability Prioritization across Container Versions
        </h1>

        <p className="ipx-sub">
          CHRONOS maps fast-moving container releases, recalibrates CVE risk across versions, and
          builds CI/CD-ready remediation plans.
        </p>

        <p className="ipx-hero-body">
          Based on ~10k container image repositories and ~260k image versions, CHRONOS unites
          evolution-aware risk scoring with constraint-aware planning.
        </p>

        <p className="ipx-hero-body">
          In this website, we only present a representative subset of our complete dataset for
          exploration (20 container image repositories and ~2k image versions).
        </p>

        <div className="ipx-actions">
          <button
            className="ipx-btn ipx-btn--primary"
            onClick={() => navigate("/")}
          >
            Go to Analysis
          </button>
        </div>
      </header>

      <section className="ipx-section">
        <h2 className="ipx-section-title">The Problem</h2>
        <ul className="ipx-list">
          <li>
            Modern CI/CD processes compress patching windows from months to hours.
          </li>
          <li>
            As versions are shipped, a CVE’s real risk can decrease (e.g., debloating), increase
            (e.g., chaining/propagation), or appear/disappear across versions — but most triage is
            still version-blind and static (e.g., CVSS cutoffs).
          </li>
          <li>
            The result: wasted analyst time, missed chains, and supposedly “optimal” plans that are
            actually sub-optimal across releases.
          </li>
        </ul>
      </section>

      <section className="ipx-grid">
        <article className="ipx-card">
          <h3>What CHRONOS Does</h3>
          <ul className="ipx-list">
            <li>
              <strong>🌀 Evolution-Aware Risk:</strong> Combines intra-version risk (e.g., CVSS,
              exploitability, exploit status) with inter-version risk (e.g., package drift,
              chaining, debloating) to appropriately score CVEs across evolving versions.
            </li>
            <li>
              <strong>⚖️ Constraint-Aware Remediation:</strong> Simulates analyst hours, skills,
              and CI/CD release windows to assign who patches what, and when, while balancing the
              security and efficiency requirements of the CI/CD pipeline.
            </li>
          </ul>
        </article>

        <article className="ipx-card">
          <h3>Benefit of CHRONOS vs Baseline</h3>
          <ul className="ipx-list">
            <li>Residual Risk ↓</li>
            <li>Analyst Hours Saved ↑ (Deployment Gap ↓)</li>
            <li>Time-to-Ready Balanced ⚡</li>
          </ul>
        </article>
      </section>

      <section className="ipx-section">
        <h2 className="ipx-section-title">Who Is This Website Useful For?</h2>
        <ul className="ipx-list">
          <li>Security researchers</li>
          <li>Vulnerability management (VM) teams</li>
          <li>DevSecOps &amp; SREs</li>
          <li>CISOs &amp; Risk / GRC leaders</li>
          <li>Tool vendors</li>
          <li>Educators &amp; Students</li>
        </ul>
      </section>

      <section className="ipx-grid">
        <article className="ipx-card">
          <h3>Features</h3>
          <ul className="ipx-list">
            <li>
              <strong>Drilldown explorer:</strong> Docker image repositories → image versions → CVEs.
            </li>
            <li>
              <strong>Evolution-aware risk:</strong> intra-version + inter-version risk scoring.
            </li>
            <li>
              <strong>Constraint-aware remediation:</strong> analyst/time/resource-aware planning.
            </li>
            <li>
              <strong>Baseline comparison:</strong> toggle baselines related to security constraints
              (high risk, medium risk, low risk, minimal risk) and efficiency constraints (half day,
              full day, 2-day, 1-week deployment windows), seeing the solution in action in varied
              environments.
            </li>
          </ul>
        </article>

        <article className="ipx-card">
          <h3>Roadmap (Planned Features)</h3>
          <ul className="ipx-list">
            <li>
              <strong>What-if sandbox:</strong> adjust various factors of the environment (analyst
              pool, SLA, skill matrix) and CI/CD constraints (security/efficiency constraints) in a
              fine-grained way, creating custom organization profiles and environments.
            </li>
            <li>
              <strong>API &amp; export:</strong> CSV/textual data export for the current view.
            </li>
            <li>(More coming soon.)</li>
          </ul>
        </article>
      </section>



      <section className="ipx-footer">
        <article className="ipx-card">
          <h3>Cite CHRONOS</h3>
          <pre aria-label="BibTeX">
{`@inproceedings{chronos2026,
  author    = {...},
  title     = {Fast Pipelines, Evolving Risks: Evolution-Aware Vulnerability Prioritization across Container Versions},
  booktitle = {...},
  year      = {2026},
}`}
          </pre>
        </article>
      </section>
    </div>
  );
}
